import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import SectionHeader from '../../../components/SectionHeader';
import Button from '../../../components/buttons/Button';
import { useMemo, useRef } from 'react';
import { scrollToError } from '../../../utils/HelperFunctions';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ILocation } from '../types/interfaces';
import FormGalleryUpload from '../../../components/formInputs/FormGalleryUpload';
import LocationWorkingHours from './LocationWorkingHours/LocationWorkingHours';
import useFormsIntegrationHelpers from '../../../hooks/useFormsIntegrationHelpers';
import SwitchActivation from '../../../components/layout/SwitchActivation';
import { useQuery } from '@tanstack/react-query';
import { generalGet } from '../../../API/api';
import { customStyles } from '../../../utils/SelectStyles';
import { useMediaQuery } from 'react-responsive';
import FormDynamicSkeleton from '../../../components/loaders/FormDynamicSkeleton';

const CreateLocationForm = () => {
  const { id } = useParams();
  const formRef = useRef(null);
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery({ query: '(max-width: 1024px)' });

  const { apiData, getDataLoading, handleSubmit, submitLoading } = useFormsIntegrationHelpers({
    queryKey: ['locations', id],
    invalidateQueryKey: ["locations"],
    id,
    singleGetApi: `/admin/locations/${id}`,
    addApi: '/admin/locations',
    editApi: `/admin/locations/${id}`,
    listRoute: '/locations',
    itemName: t('location')
  });

  const validationSchema = Yup.object({
    name: Yup.object().shape({
      ar: Yup.string().required(t('required')).min(3, t('name_short')),
      en: Yup.string().required(t('required')).min(3, t('name_short'))
    })
  });

  const { data: citiesData } = useQuery({
    queryKey: ['cities'],
    queryFn: () => generalGet('/admin/cities')
  });

  console.log(citiesData, "citiesData")

  const citiesOptions = useMemo(() => {
    return citiesData?.data?.data?.map((item: any) => ({
      label: item.name?.[`${i18n.language || 'en'}`],
      value: item.id
    }));
  }, [citiesData, i18n.language]);

  const usedOptions = useMemo(() => {
    return [
      { label: t('new'), value: 1 },
      { label: t('used'), value: 2 },
      { label: t('new_used'), value: 3 }
    ];
  }, []);

  const initialValues: ILocation = {
    ...apiData,
    
    name: {
      en: apiData?.name?.en || '',
      ar: apiData?.name?.ar || ''
    },
    description: {
      en: apiData?.description?.en || '',
      ar: apiData?.description?.ar || ''
    },
    address: {
      en: apiData?.address?.en || '',
      ar: apiData?.address?.ar || ''
    },
    
    hidden: apiData?.hidden !== undefined ? (apiData.hidden ? 1 : 0) : 0,
    service_center: apiData?.service_center !== undefined ? (apiData.service_center ? 1 : 0) : 0,
    parts: apiData?.parts !== undefined ? (apiData.parts ? 1 : 0) : 0,
    quick_service: apiData?.quick_service !== undefined ? (apiData.quick_service ? 1 : 0) : 0,
    showroom: apiData?.showroom !== undefined ? (apiData.showroom ? 1 : 0) : 0,
    workshop: apiData?.workshop !== undefined ? (apiData.workshop ? 1 : 0) : 0,
    bodyshop: apiData?.bodyshop !== undefined ? (apiData.bodyshop ? 1 : 0) : 0,
    
    lat: apiData?.lat || 0,
    long: apiData?.long || 0,
    maps_link: apiData?.maps_link || '',
    google_id: apiData?.google_id || '',
    phone: apiData?.phone || 0,
    type: apiData?.type || '',
    used: apiData?.used || 0,
    
    city_id: apiData?.city?.id || '',
    images: apiData?.media?.map((item: any) => item?.url) || [],
    working_hours: apiData?.working_hours || [],
    image_orders: []
  };

  if (getDataLoading)
    return (
      <FormDynamicSkeleton
        sections={[
          [
            { columns: 2, items: 2 },
            { columns: 2, items: 2 },
            { columns: 2, items: 2 },
            { columns: 2, items: 2 },
            { columns: 2, items: 2, type: 'textarea' }
          ],
          [
            { columns: 4, items: 4, type: 'switch' },
            { columns: 4, items: 3, type: 'switch' },
          ],
          [
            { columns: 2, items: 2 },
            { columns: 2, items: 2 },
          ],
          [
            { columns: 2, items: 2 },
            { columns: 2, items: 2 },
          ],
          [
            { columns: 1, items: 1, type: 'textarea' },
          ],
        ]}
        buttons={1}
      />
    );

  return (
    <div className={'form_section'} ref={formRef}>
      <Formik
        validateOnMount
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setErrors }) => {
          const newValues = { ...values };
          newValues.image_orders = newValues.images.map((_, index: number) => index + 1);
          let value = { ...values, _method: 'POST' };
          if (id) {
            value = { ...value, _method: 'PUT' };
          }
          handleSubmit({ ...value }, setErrors);
        }}
      >
        {(formik) => {
          return (
            <Form className="locations_form">
              <SectionHeader title={t('location_details')}>
                <div className="inputs_group">
                  <FieldWrapper title={t('name_en')} inputName={`name[en]`} inputPlaceholder={t('name_en')} input customPadding />
                  <FieldWrapper title={t('name_ar')} inputName={`name[ar]`} inputPlaceholder={t('name_ar')} input customPadding />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('address_en')} inputName={`address[en]`} inputPlaceholder={t('address_en')} input customPadding />
                  <FieldWrapper title={t('address_ar')} inputName={`address[ar]`} inputPlaceholder={t('address_ar')} input customPadding />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('code')} inputName={`location_code`} inputPlaceholder={t('code')} input customPadding />
                  <FieldWrapper customPadding title={t('city')} inputPlaceholder={t('city')} inputName={`city_id`} options={citiesOptions} selectStyle={customStyles} select />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('phone')} inputName={`phone`} inputPlaceholder={t('phone')} input customPadding />
                  <FieldWrapper customPadding title={t('used')} inputPlaceholder={t('used')} inputName={`used`} options={usedOptions} selectStyle={customStyles} select />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('description_en')} inputName={`description[en]`} inputPlaceholder={t('description_en')} textArea customPadding />
                  <FieldWrapper title={t('description_ar')} inputName={`description[ar]`} inputPlaceholder={t('description_ar')} textArea customPadding />
                </div>
              </SectionHeader>

              <SectionHeader title={t('location_info')} customStyle="locations_form_switchs">
                <div className="inputs_group">
                  <div className="switch_container">
                    <SwitchActivation onChange={(value: any) => formik.setFieldValue('parts', value?.target?.checked ? 1 : 0)} active={formik.values.parts} />
                    <p>{t('parts')}</p>
                  </div>
                  <div className="switch_container">
                    <SwitchActivation onChange={(value: any) => formik.setFieldValue('service_center', value?.target?.checked ? 1 : 0)} active={formik.values.service_center} />
                    <p>{t('service_center')}</p>
                  </div>
                  <div className="switch_container">
                    <SwitchActivation onChange={(value: any) => formik.setFieldValue('showroom', value?.target?.checked ? 1 : 0)} active={formik.values.showroom} />
                    <p>{t('showroom')}</p>
                  </div>
                  <div className="switch_container">
                    <SwitchActivation onChange={(value: any) => formik.setFieldValue('quick_service', value?.target?.checked ? 1 : 0)} active={formik.values.quick_service} />
                    <p>{t('quick_service')}</p>
                  </div>
                </div>
                <div className="inputs_group">
                  <div className="switch_container">
                    <SwitchActivation onChange={(value: any) => formik.setFieldValue('workshop', value?.target?.checked ? 1 : 0)} active={formik.values.workshop} />
                    <p>{t('workshop')}</p>
                  </div>
                  <div className="switch_container">
                    <SwitchActivation onChange={(value: any) => formik.setFieldValue('bodyshop', value?.target?.checked ? 1 : 0)} active={formik.values.bodyshop} />
                    <p>{t('bodyshop')}</p>
                  </div>
                  <div className="switch_container">
                    <SwitchActivation onChange={(value: any) => formik.setFieldValue('hidden', value?.target?.checked ? 1 : 0)} active={formik.values.hidden} />
                    <p>{t('hidden')}</p>
                  </div>
                  {!isMobile && <div className="switch_container"></div>}
                </div>
              </SectionHeader>
              <SectionHeader title={t('location_place_details')}>
                <div className="inputs_group">
                  <FieldWrapper title={t('lat')} inputName={`lat`} inputPlaceholder={t('lat')} input customPadding />
                  <FieldWrapper title={t('lng')} inputName={`long`} inputPlaceholder={t('lng')} input customPadding />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('maps_link')} inputName={`maps_link`} inputPlaceholder={t('maps_link')} input customPadding />
                  <FieldWrapper title={t('google_id')} inputName={`google_id`} inputPlaceholder={t('google_id')} input customPadding />
                </div>
              </SectionHeader>
              <LocationWorkingHours />
              <SectionHeader title={t('gallery')} customStyle="last">
                <div className="inputs_group">
                  <FormGalleryUpload errors={formik?.errors.images as string} touched={formik?.touched?.images as boolean} formik={formik} name={`images`} />
                </div>
              </SectionHeader>
              <div className="form_button reverse">
                <Button
                  loading={submitLoading}
                  onClick={() => {
                    scrollToError(!formik.isValid, formRef);
                  }}
                >
                  <span className="bold">{t('save')}</span>
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default CreateLocationForm;
